import { Controller } from "@hotwired/stimulus"
import { showSystemToast } from "../utils/system_toast"
import { scrollThreadToEnd } from "../utils/thread_scroll"

// The Messages sidebar: a right drawer opened from the header chat icon. Body
// (online teammates + recent conversations) is lazy-loaded on first open so it
// never costs anything on an ordinary page load. Presence dots inside are kept
// live by the separate `presence` controller.
const MESSAGE_VIEWS = ["sidebar", "drawer", "floating"]

export default class extends Controller {
    static targets = ["panel", "body", "search", "title", "presence", "actions", "avatar", "avatarImage", "avatarInitials", "avatarIcon", "fullscreen"]

    connect() {
        this._loaded = false
        this._threadUrl = null
        this._listHTML = null
        this._onDocClick = (event) => this._maybeCloseOnOutside(event)
        this._onKeydown = (event) => { if (event.key === "Escape") this._onEscape() }
        // Cross-widget open hook (#463): universal search's persistent "New
        // message" row dispatches this instead of calling an action directly,
        // so it needs no knowledge of the drawer at all. Empty detail = plain
        // compose(); a populated one mirrors composeTo()'s own contract.
        this._onJaneCompose = (event) => this._handleJaneCompose(event)
        window.addEventListener("jane:compose", this._onJaneCompose)
        // The Jane avatar's "All messages" (and its "+N"): the header button's job.
        this._onJaneMessages = () => this.open()
        window.addEventListener("jane:messages", this._onJaneMessages)
        this._onJaneMessagesClose = () => this.close()
        window.addEventListener("jane:messages-close", this._onJaneMessagesClose)
        // Restore the saved presentation (sidebar / drawer / floating).
        this._applyView(this._savedView())
        // Floating window is draggable by its header.
        this._head = this.hasPanelTarget ? this.panelTarget.querySelector(".messages-drawer-head") : null
        if (this._head) this._head.addEventListener("pointerdown", (event) => this._startDrag(event))
        // Compose-in-place: intercept any thread composer submit and reload the
        // thread fragment (mirrors the Jane sidebar so the user never leaves).
        if (this.hasPanelTarget) {
            this.panelTarget.addEventListener("submit", (event) => this._onComposeSubmit(event))
            // assistant-compose sends Jane messages itself (with attachments);
            // follow the thread it landed in rather than leaving the page.
            this.panelTarget.addEventListener("assistant-compose:sent", (event) => this._onComposeSent(event))
        }
        this._autostartCompose()
    }

    // /contacts/:id?compose=1 lands with the composer already open on that
    // person - it is where the Chrome extension's Message button goes, since
    // the extension cannot host this drawer itself. The card marks its own
    // Message button (contacts/_card_actions); the param is dropped once used
    // so a reload does not reopen it.
    _autostartCompose() {
        const button = document.querySelector("[data-compose-autostart]")
        if (!button) return

        const url = new URL(window.location.href)
        url.searchParams.delete("compose")
        window.history.replaceState(window.history.state, "", url.toString())
        this._openComposeThread({
            url: button.dataset.composeUrl,
            title: button.dataset.composeTitle,
            avatar: button.dataset.composeAvatar
        })
    }

    disconnect() {
        this._teardownListeners()
        window.removeEventListener("jane:compose", this._onJaneCompose)
        window.removeEventListener("jane:messages", this._onJaneMessages)
        window.removeEventListener("jane:messages-close", this._onJaneMessagesClose)
    }

    open(event) {
        if (event) event.preventDefault()
        if (!this.hasPanelTarget) return

        this._load()
        this.panelTarget.classList.remove("hidden")
        this.panelTarget.setAttribute("aria-hidden", "false")
        document.body.classList.add("messages-drawer-active")
        this._syncDocked()
        // Defer listener attach so the opening click doesn't immediately close it.
        window.requestAnimationFrame(() => {
            document.addEventListener("click", this._onDocClick)
            document.addEventListener("keydown", this._onKeydown)
            if (this.hasSearchTarget) this.searchTarget.focus()
        })
    }

    close() {
        if (!this.hasPanelTarget) return
        this._setFullscreen(false)
        if (this._threadUrl) this.back() // reopen should land on the list
        this.panelTarget.classList.add("hidden")
        this.panelTarget.setAttribute("aria-hidden", "true")
        document.body.classList.remove("messages-drawer-active")
        this._syncDocked()
        this._teardownListeners()
    }

    toggle(event) {
        if (this.panelTarget.classList.contains("hidden")) this.open(event)
        else this.close()
    }

    // --- lazy load ---
    async _load() {
        if (this._loaded || !this.hasBodyTarget) return
        const url = this.panelTarget.dataset.messagesPanelUrl
        if (!url) return
        this._loaded = true
        try {
            const response = await fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin" })
            if (!response.ok) throw new Error("load failed")
            const html = await response.text()
            // composeTo() can switch into thread mode before this resolves
            // (it opens the drawer straight onto a contact, skipping the list
            // entirely) — don't stomp on the thread that's now showing, just
            // stash the list for when Back needs it.
            if (this._threadUrl) {
                this._listHTML = html
                return
            }
            this.bodyTarget.innerHTML = html
        } catch (_error) {
            this._loaded = false
            this.bodyTarget.innerHTML = `<p class="messages-drawer-loading">Couldn't load messages. Try again.</p>`
        }
    }

    // --- search (client-side filter of the loaded rows) ---
    filter() {
        if (this._threadUrl) return // search is a list-only affordance
        const q = this.searchTarget.value.trim().toLowerCase()
        this.bodyTarget.querySelectorAll("[data-messages-row]").forEach((row) => {
            const match = q === "" || row.textContent.toLowerCase().includes(q)
            row.style.display = match ? "" : "none"
        })
    }

    // --- inline thread view (stay in the widget) ---
    openThread(event) {
        const url = event.currentTarget.dataset.threadUrl
        if (!url) return // no inline fragment: let the link navigate (fallback)
        event.preventDefault()
        // Rows inside the panel only ever fire with it open; a page's own
        // Message button (a profile's) opens it first.
        if (this.panelTarget.classList.contains("hidden")) this.open()

        // Cache the list so Back is instant.
        if (!this._threadUrl) this._listHTML = this.bodyTarget.innerHTML
        this._threadUrl = url
        this.panelTarget.classList.add("messages-drawer--thread")
        if (this.hasTitleTarget) this.titleTarget.textContent = event.currentTarget.dataset.threadTitle || "Conversation"
        this._setHeaderAvatar(event.currentTarget.dataset)
        this._setPresence(event.currentTarget.dataset.threadOnline === "true")
        this._loadThread()
    }

    back() {
        if (!this._threadUrl) return
        this._stopThreadPin?.()
        this._stopThreadPin = null
        this._threadUrl = null
        this.panelTarget.classList.remove("messages-drawer--thread")
        if (this.hasTitleTarget) this.titleTarget.textContent = "Inbox"
        this._setHeaderAvatar(null)
        this._setPresence(false)
        this._removeAdoptedMenu()
        if (this._listHTML != null) this.bodyTarget.innerHTML = this._listHTML
    }

    async _loadThread() {
        try {
            const response = await fetch(this._threadUrl, { headers: { Accept: "text/html" }, credentials: "same-origin" })
            if (!response.ok) throw new Error("thread load failed")
            this.bodyTarget.innerHTML = await response.text()
            this._adoptThreadMenu()
            this._scrollThreadToEnd()
        } catch (_error) {
            this.bodyTarget.innerHTML = `<p class="messages-drawer-loading">Couldn't load that conversation.</p>`
        }
    }

    // The face beside the drawer title. Rows carry their own already-resolved
    // avatar (assistant rows the thread PERSONA's face, DM/channel rows the
    // peer's picture) plus the fallback they themselves render, so the header
    // never has to guess and never shows a broken image. `dataset === null`
    // resets it, which is what going Back to the Messages list wants.
    _setHeaderAvatar(dataset) {
        if (!this.hasAvatarTarget) return
        const url = dataset?.threadAvatar || ""
        const initials = dataset?.threadInitials || ""
        const icon = dataset?.threadIcon || ""
        const sparkle = dataset?.threadSparkle === "true"

        if (this.hasAvatarImageTarget) {
            const img = this.avatarImageTarget
            // The sparkle is the themed app mark; tag it so a later theme
            // toggle re-points it (theme.js syncs [data-theme-logo]).
            if (url) {
                img.removeAttribute("data-theme-logo")
                img.src = url
            } else if (sparkle) {
                img.setAttribute("data-theme-logo", "true")
                img.src = document.documentElement.getAttribute("data-bs-theme") === "dark" ? "/favicon-night.svg" : "/favicon-day.svg"
            } else {
                img.removeAttribute("data-theme-logo")
                img.removeAttribute("src") // never leave a dangling request behind
            }
            img.hidden = !(url || sparkle)
        }
        if (this.hasAvatarInitialsTarget) {
            this.avatarInitialsTarget.textContent = initials
            this.avatarInitialsTarget.hidden = !(!url && !sparkle && initials)
        }
        if (this.hasAvatarIconTarget) {
            const el = this.avatarIconTarget
            el.className = `bi messages-drawer-avatar-icon ${icon}`.trim()
            el.hidden = !(!url && !sparkle && !initials && icon)
        }
        this.avatarTarget.classList.toggle("is-jane", Boolean(sparkle))
        this.avatarTarget.hidden = !(url || sparkle || initials || icon)
    }

    // Green "online" dot next to the drawer title (Jane is always online;
    // teammates per presence at open time).
    _setPresence(online) {
        if (this.hasPresenceTarget) this.presenceTarget.hidden = !online
    }

    // The loaded thread fragment carries its own header with a conversation
    // kebab (.msg-detail-menu). The drawer hides that inner header (CSS) to
    // avoid a double header, and adopts the kebab into its own actions bar so
    // the menu sits left of the close button.
    _adoptThreadMenu() {
        this._removeAdoptedMenu()
        if (!this.hasActionsTarget) return
        const menu = this.bodyTarget.querySelector(".msg-detail-head .msg-detail-menu")
        if (!menu) return
        menu.classList.add("messages-drawer-adopted-menu")
        this.actionsTarget.insertBefore(menu, this.actionsTarget.querySelector(".messages-drawer-close"))
    }

    _removeAdoptedMenu() {
        this.element.querySelectorAll(".messages-drawer-adopted-menu").forEach((el) => el.remove())
    }

    // A bare scrollTop assignment here landed short whenever the thread carried
    // avatars or images — they have no height at swap time, so the content grew
    // past the scroll. The shared helper re-pins until the layout settles.
    _scrollThreadToEnd() {
        this._stopThreadPin?.()
        this._stopThreadPin = scrollThreadToEnd(this.bodyTarget)
    }

    // Post any composer inside a loaded thread via fetch, then reload the
    // fragment in place. Handles both Jane (assistant, JSON reply) and teammate
    // DMs (connection, redirect) uniformly.
    _onComposeSubmit(event) {
        if (!this._threadUrl) return
        // A composer that already handled its own submit (universal-compose
        // fetches non-Jane destinations itself) must not be sent twice.
        if (event.defaultPrevented) return
        const form = event.target.closest("form")
        if (!form) return
        event.preventDefault()

        const body = new FormData(form)
        const field = form.querySelector(".assistant-compose-field, input[type=text], textarea")
        if (field && !String(field.value || "").trim()) return
        if (field) field.value = ""

        fetch(form.action, {
            method: (form.method || "post").toUpperCase(),
            headers: { "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "", Accept: "application/json, text/html" },
            body: body,
            credentials: "same-origin"
        })
            .then((r) => (r.ok ? r.text() : null))
            .then((text) => {
                // Fresh Jane chat: capture the new conversation id so the reload targets it.
                try {
                    const data = JSON.parse(text)
                    if (data?.conversation_id && !this._threadUrl.includes("conversation_id=")) {
                        this._threadUrl += (this._threadUrl.includes("?") ? "&" : "?") + "conversation_id=" + data.conversation_id
                    }
                } catch (_error) { /* connection posts return HTML, not JSON */ }
            })
            .catch(() => {})
            .finally(() => this._loadThread())
    }

    _onComposeSent(event) {
        if (!this._threadUrl) return
        const id = event.detail?.conversationId
        if (id && !this._threadUrl.includes("conversation_id=")) {
            this._threadUrl += (this._threadUrl.includes("?") ? "&" : "?") + "conversation_id=" + id
        }
        this._loadThread()
    }

    _onEscape() {
        if (this._fullscreen) this._setFullscreen(false)
        else if (this._threadUrl) this.back()
        else this.close()
    }

    // Full screen on top of whatever view it is in, like the profile avatar's
    // Expand; Escape or the same button brings it back.
    toggleFullscreen(event) {
        if (event) event.preventDefault()
        this._setFullscreen(!this._fullscreen)
    }

    _setFullscreen(on) {
        if (!this.hasPanelTarget) return
        this._fullscreen = on
        this.panelTarget.classList.toggle("messages-drawer--fullscreen", on)
        if (this.hasFullscreenTarget) {
            this.fullscreenTarget.setAttribute("aria-pressed", String(on))
            this.fullscreenTarget.title = on ? "Exit full screen" : "Full screen"
            this.fullscreenTarget.setAttribute("aria-label", this.fullscreenTarget.title)
            const icon = this.fullscreenTarget.querySelector("i")
            if (icon) icon.className = `bi ${on ? "bi-arrows-angle-contract" : "bi-arrows-angle-expand"}`
        }
    }

    // --- view-as menu: drawer / popover / floating ---
    setView(event) {
        const view = event.currentTarget.dataset.view
        this._applyView(view)
        try { window.localStorage.setItem("emailpa-messages-view", view) } catch (_error) { /* ignore */ }
    }

    // Sidebar by default, like every other panel on the right; the old
    // "popover from nav" had no nav button left to pop from, so it reads as
    // the drawer.
    _savedView() {
        try {
            const saved = window.localStorage.getItem("emailpa-messages-view")
            return saved === "popover" ? "drawer" : saved || "sidebar"
        } catch (_error) {
            return "sidebar"
        }
    }

    _applyView(view) {
        if (!this.hasPanelTarget) return
        const mode = MESSAGE_VIEWS.includes(view) ? view : "sidebar"
        this._mode = mode
        MESSAGE_VIEWS.forEach((m) => {
            this.panelTarget.classList.toggle(`messages-drawer--${m}`, m === mode)
        })
        this._syncDocked()
        // Clear any leftover floating drag offset when switching modes.
        this.panelTarget.style.top = ""
        this.panelTarget.style.left = ""
        this.panelTarget.style.right = ""
        this.panelTarget.style.transform = ""
        this.element.querySelectorAll(".messages-view-panel .assistant-header-menu-item[data-view]").forEach((item) => {
            item.classList.toggle("is-active", item.dataset.view === mode)
        })
    }

    // Drag the floating window by its header.
    _startDrag(event) {
        if (!this.panelTarget.classList.contains("messages-drawer--floating")) return
        if (event.target.closest("button, a")) return

        const rect = this.panelTarget.getBoundingClientRect()
        const offX = event.clientX - rect.left
        const offY = event.clientY - rect.top
        this.panelTarget.style.transform = "none"
        this.panelTarget.style.right = "auto"
        this.panelTarget.style.left = `${rect.left}px`
        this.panelTarget.style.top = `${rect.top}px`

        const move = (ev) => {
            this.panelTarget.style.left = `${ev.clientX - offX}px`
            this.panelTarget.style.top = `${ev.clientY - offY}px`
        }
        const up = () => {
            document.removeEventListener("pointermove", move)
            document.removeEventListener("pointerup", up)
        }
        document.addEventListener("pointermove", move)
        document.addEventListener("pointerup", up)
    }

    toggleDensity(event) {
        const compact = this.panelTarget.classList.toggle("messages-drawer--compact")
        event.currentTarget.classList.toggle("is-active", compact)
    }

    markAllRead() {
        this.bodyTarget.querySelectorAll(".messages-recent-unread").forEach((badge) => badge.remove())
        showSystemToast("All caught up", { level: "success" })
    }

    // "New message" opens the universal composer in place — Jane, a person on
    // a channel, or an audience — instead of navigating to the Jane thread.
    // After a Jane send creates a conversation, _onComposeSubmit appends its
    // id to this URL and /composer/new hands off to the real thread fragment.
    compose() {
        if (!this._threadUrl) this._listHTML = this.bodyTarget.innerHTML
        this._threadUrl = "/composer/new"
        this.panelTarget.classList.add("messages-drawer--thread")
        if (this.hasTitleTarget) this.titleTarget.textContent = "New message"
        this._setHeaderAvatar({ threadSparkle: "true" })
        this._setPresence(false)
        this._loadThread()
    }

    // Same drawer-composer flow as compose(), but for a caller that names its
    // own destination + title instead of the bare /composer/new — a contact
    // card's "Message" action (#463) opens straight onto that contact,
    // prefilled, via GET /composer/new?contact_id=…. Unlike compose() (fired
    // from a button already inside the open drawer) this can run from any
    // page, so it also opens the drawer itself first.
    composeTo(event) {
        if (event) event.preventDefault()
        const url = event?.currentTarget?.dataset?.composeUrl
        if (!url) return
        this._openComposeThread({
            url,
            title: event.currentTarget.dataset.composeTitle,
            avatar: event.currentTarget.dataset.composeAvatar
        })
    }

    // A link inside the open compose view that swaps it for another one on
    // the same person - the email card's "Or message Teresa on LinkedIn"
    // (composers/email). Same drawer, same header, new body.
    loadCompose(event) {
        const url = event?.currentTarget?.getAttribute("href")
        if (!url || !this._threadUrl) return
        event.preventDefault()
        this._threadUrl = url
        this._loadThread()
    }

    // window "jane:compose" handler: a detail-less dispatch (universal
    // search's persistent row) is the plain composer; a populated one
    // ({url, title, avatar}) mirrors composeTo()'s own dataset contract for
    // callers further from the drawer than a Stimulus action can reach.
    _handleJaneCompose(event) {
        if (!this.hasPanelTarget) return
        const detail = event.detail || {}
        if (detail.url) {
            this._openComposeThread(detail)
        } else {
            this.open()
            this.compose()
        }
    }

    _openComposeThread({ url, title, avatar } = {}) {
        if (!this.hasPanelTarget || !url) return
        this.open()
        if (!this._threadUrl) this._listHTML = this.bodyTarget.innerHTML
        this._threadUrl = url
        this.panelTarget.classList.add("messages-drawer--thread")
        if (this.hasTitleTarget) this.titleTarget.textContent = title || "New message"
        this._setHeaderAvatar(avatar ? { threadAvatar: avatar } : { threadSparkle: "true" })
        this._setPresence(false)
        this._loadThread()
    }

    // A docked sidebar is part of the page: clicking the page doesn't close
    // it. The drawer and the floating window still close on an outside click
    // — but never on the Jane avatar, whose faces open threads in here.
    _maybeCloseOnOutside(event) {
        if (this.panelTarget.contains(event.target)) return
        if (event.target.closest(".messages-trigger, #jane-avatar, [data-action*=\"messages-sidebar#\"]")) return
        if (this._mode === "sidebar") return
        this.close()
    }

    // html.messages-docked makes room for the sidebar and tucks the page's own
    // right sidebar away (one right sidebar at a time); see _messages_sidebar.scss.
    _syncDocked() {
        if (!this.hasPanelTarget) return
        const docked = this._mode === "sidebar" && !this.panelTarget.classList.contains("hidden")
        document.documentElement.classList.toggle("messages-docked", docked)
        window.dispatchEvent(new CustomEvent("jane:panels-changed"))
    }

    _teardownListeners() {
        document.removeEventListener("click", this._onDocClick)
        document.removeEventListener("keydown", this._onKeydown)
    }
}
